import { questionnaireQuestionTypeConstante } from "@/constantes/Questionnaire/questionnaire-question-type";
import { i18n } from "@/i18n";
import { questionnaireManager } from "@/services/questionnaire/questionnaire-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { usePlatformStore } from "@/store/platform/platform";
import { defineStore } from "pinia";
import {
  QUESTIONNAIRE_EDITOR_STORAGE_KEY,
  persistQuestionnaireEditor,
  cancelPersistQuestionnaireEditor,
} from "@/store/studea_manager/questionnaire-editor-persistence";

// Ré-exportées pour compatibilité avec les imports existants (vue/tests) qui lisent ces
// symboles depuis le store plutôt que depuis le module de persistance lui-même.
export { QUESTIONNAIRE_EDITOR_STORAGE_KEY, cancelPersistQuestionnaireEditor };

// Base commune aux 6 listes de profils de l'éditeur de questionnaire (signedBy, visibleBy,
// marksVisibleBy, interviewDateVisibleBy, canBeCopiedBy, versionItemMarksVisibleBy) : les 3
// profils triad, plus le profil Studea manager pour celles qui en ont besoin.
function buildProfileChoices(includeStudeaManager = false, studeaManagerLabel = null) {
  const choices = [
    { label: usePlatformStore().apprenticeProfileLabel.title, value: "1", data: 1, checked: false },
    { label: usePlatformStore().tutorProfileLabel.title, value: "2", data: 2, checked: false },
    { label: usePlatformStore().apprenticeMasterProfileLabel.title, value: "3", data: 3, checked: false },
  ];
  if (includeStudeaManager) {
    choices.push({ label: studeaManagerLabel, value: "4", data: 4, checked: false });
  }
  return choices;
}

// Variante utilisée par questionnaireNew() : valeurs numériques (au lieu de string) et libellé
// du 4e profil toujours issu du store plateforme (au lieu de l'i18n générique utilisé par
// initQuestionnaireChoices pour signedBy/interviewDateVisibleBy) — schéma distinct volontairement
// non fusionné avec buildProfileChoices pour ne pas changer la valeur initiale d'un questionnaire.
function buildNewQuestionnaireProfileChoices(includeStudeaManager = false, extra = {}) {
  const choices = [
    { label: usePlatformStore().apprenticeProfileLabel.title, value: 1, data: 1, checked: false, ...extra },
    { label: usePlatformStore().tutorProfileLabel.title, value: 2, data: 2, checked: false, ...extra },
    { label: usePlatformStore().apprenticeMasterProfileLabel.title, value: 3, data: 3, checked: false, ...extra },
  ];
  if (includeStudeaManager) {
    choices.push({
      label: usePlatformStore().studeaManagerProfileLabel.title,
      value: 4,
      data: 4,
      checked: false,
      ...extra,
    });
  }
  return choices;
}

// Ligne de grille par défaut (questionnaireAdd, type "grid" avec displayMode = 2) : les deux
// premières colonnes d'un cluster grille neuf sont strictement identiques hormis leur index.
function buildGridRowQuestion(index, access) {
  return {
    id: null,
    index: index,
    title: "",
    access: access,
    tip: "",
    tipActive: false,
    required: 0,
    selected: true,
    comments: false,
    commentsLabel: "",
    commentsType: 1,
    displayMark: false,
    displayRadar: false,
    maximalMark: "",
    openedQuestionsAccess: [],
    openedQuestions: false,
    openedQuestionsType: 0,
    fieldSkills: [],
  };
}

// Reporte l'état "checked" d'une valeur déjà enregistrée (tableau de nombres ou tableau
// d'objets {data, checked}, selon l'origine des données) sur une liste de choix fraîchement
// construite par buildProfileChoices.
function applyProfileChoicesChecked(choices, existingValue) {
  if (!existingValue) {
    return choices;
  }
  // Sécurise le cas où l'API sérialise la valeur sous forme d'objet-map plutôt que de tableau.
  if (!Array.isArray(existingValue)) {
    existingValue = Object.values(existingValue);
  }
  const isPlainValueArray = typeof existingValue[0] !== "object";
  choices.forEach((choice) => {
    if (isPlainValueArray) {
      choice.checked = existingValue.includes(choice.data);
    } else {
      const item = existingValue.find((item) => item.data === choice.data);
      choice.checked = item && item.checked;
    }
  });
  return choices;
}

export const useStudeaManagerQuestionnaireStore = defineStore({
  id: "StudeaManagerQuestionnaire",
  state: () => {
    return {
      questionnaire: {},
      lastSavedVersion: {},
      lastIndex: 0,
      questionnaires: {},
      updated: 0,
    };
  },

  actions: {
    init() {
      this.questionnaire = {};
      this.lastSavedVersion = {};
      this.lastIndex = 0;
      this.questionnaires = {};
      this.updated = 0;
    },

    findQuestionnaireItem(list, index) {
      return Object.values(list).find((item) => item.index === index);
    },

    initLastIndex(index) {
      this.lastIndex = index;
    },

    retrieveNew() {
      if (window.localStorage.getItem(QUESTIONNAIRE_EDITOR_STORAGE_KEY)) {
        this.questionnaire = JSON.parse(window.localStorage.getItem(QUESTIONNAIRE_EDITOR_STORAGE_KEY));
      }
    },

    questionnaireNew() {
      this.questionnaire = {
        type: "",
        title: "",
        active: true,
        locked: false,
        useCrewDates: false,
        useInterviewDate: false,
        interviewDateLabel: "",
        useSameDates: true,
        defaultStartDate: "",
        defaultEndDate: "",
        defaultApprenticeAccessStartDate: "",
        defaultApprenticeAccessEndDate: "",
        defaultTutorAccessStartDate: "",
        defaultTutorAccessEndDate: "",
        defaultApprenticeMasterAccessStartDate: "",
        defaultApprenticeMasterAccessEndDate: "",
        defaultStudeaManagerAccessStartDate: "",
        defaultStudeaManagerAccessEndDate: "",
        completenessDate: "",
        firstReminder: "",
        secondReminder: "",
        code: "",
        secondaryQuestionnaires: [],
        description: "",
        descriptionPdf: null,
        visibleBy: buildProfileChoices(),
        signedBy: buildNewQuestionnaireProfileChoices(true, { disabled: true }),
        questions: [],
        category: null,
        trainingCourse: null,
        markAutomatic: false,
        canBeCopiedBy: buildNewQuestionnaireProfileChoices(true),
        marksVisibleBy: buildProfileChoices(),
        interviewDateVisibleBy: buildNewQuestionnaireProfileChoices(true),
        summarySheet: false,
        allProfilSummarySheet: false,
        template: null,
      };
    },

    questionnaireAdd(type) {
      this.lastIndex++;

      const signedBy = this.questionnaire.signedBy
        ? this.questionnaire.signedBy.filter((item) => item.checked)
        : buildProfileChoices();
      let access = [];
      if (signedBy.length === 1) {
        access = [signedBy[0].data];
      }

      const item = {
        id: null,
        index: this.lastIndex,
        title: "",
        type: 0,
        itemType: "field",
        access: access,
        tip: "",
        tipActive: false,
        required: true,
        selected: false,
        displayMode: 1,
        comments: false,
        commentsLabel: "",
        commentsType: 1,
        displayMark: false,
        displayRadar: false,
        maximalMark: "",
        openedQuestionsAccess: [],
        openedQuestions: false,
        openedQuestionsType: 1,
        fieldSkills: [],
      };

      switch (type) {
        case questionnaireQuestionTypeConstante.TITLE:
          item["itemType"] = "cluster";
          item["displayMode"] = 1;
          break;
        case questionnaireQuestionTypeConstante.QUESTION_CLUSTER:
          item["itemType"] = "cluster";
          item["grid"] = true;
          item["valueList"] = [
            { value: "", mark: "" },
            { value: "", mark: "" },
          ];
          item["questions"] = [
            buildGridRowQuestion(this.lastIndex + 1, access),
            buildGridRowQuestion(this.lastIndex + 2, access),
          ];
          this.initLastIndex(this.lastIndex + 2);
          item["displayMode"] = 2;
          break;
      }

      this.questionnaire["questions"].push(item);
      persistQuestionnaireEditor(this.questionnaire);
    },

    questionnaireDuplicate(payload) {
      this.lastIndex++;
      const item = this.findQuestionnaireItem(this.questionnaire.questions, payload.id);
      const clone = Object.assign({}, item);
      clone.index = this.lastIndex;
      clone.id = null;

      if (item.questions && Object.keys(item.questions).length) {
        clone.questions = [];
        Object.values(item.questions).forEach(function (child) {
          const cloneQuestion = Object.assign({}, child);
          cloneQuestion.id = null;
          clone.questions.push(cloneQuestion);
        });
      }

      if (item.valueList && Object.keys(item.valueList).length) {
        clone.valueList = [];
        Object.values(item.valueList).forEach(function (value) {
          clone.valueList.push({
            mark: value.mark,
            value: value.value,
          });

          if (clone.questions && Object.keys(clone.questions).length) {
            Object.values(clone.questions).forEach(function (child) {
              child.valueList = clone.valueList;
            });
          }
        });
      }
      this.questionnaire.questions.splice(payload.index + 1, 0, clone);
      persistQuestionnaireEditor(this.questionnaire);
    },

    questionnaireRemove(index) {
      const item = this.questionnaire.questions[index];
      if (item.id) {
        this.questionnaire["deletedQuestions"] = this.questionnaire["deletedQuestions"]
          ? this.questionnaire["deletedQuestions"]
          : [];
        this.questionnaire["deletedQuestions"].push({
          itemType: item.itemType,
          id: item.id,
        });
      }
      this.questionnaire.questions.splice(index, 1);
      persistQuestionnaireEditor(this.questionnaire);
    },

    questionnaireSelectBlock(index = null) {
      if (this.questionnaire && this.questionnaire.questions && Object.keys(this.questionnaire.questions).length) {
        Object.values(this.questionnaire.questions).forEach(function (baseItem) {
          baseItem.selected = false;
          if (baseItem.questions && Object.keys(baseItem.questions).length) {
            Object.values(baseItem.questions).forEach(function (baseChild) {
              baseChild.selected = false;
            });
          }
        });

        const item = this.findQuestionnaireItem(this.questionnaire.questions, index);
        if (item) {
          item.selected = true;

          if (item.questions && Object.keys(item.questions).length) {
            Object.values(item.questions).forEach(function (child) {
              child.selected = true;
            });
          }
        }
      }
    },

    questionnaireUpdate(payload) {
      this.questionnaire = payload.questionnaire;
      this.checkQuestionsProfiles();
      if (payload.save) {
        persistQuestionnaireEditor(this.questionnaire);
      }
      if (payload.updateKey) {
        this.updated++;
      }
    },

    checkQuestionsProfiles() {
      let signedBy = [];

      if (this.questionnaire?.signedBy) {
        Object.values(this.questionnaire.signedBy).forEach((access) => {
          if (typeof access !== "object") {
            signedBy.push(parseInt(access));
          } else if (access.checked) {
            signedBy.push(parseInt(access.value));
          }
        });
      }

      if (this.questionnaire?.questions && Object.keys(this.questionnaire.questions).length) {
        Object.values(this.questionnaire.questions).forEach((question) => {
          if (question.itemType === "field") {
            question.access = question.access.filter((item) => {
              return signedBy.indexOf(item) > -1;
            });
          } else if (question.itemType === "cluster" && question?.questions) {
            Object.values(question.questions).forEach((question) => {
              question.access = question.access.filter((item) => {
                return signedBy.indexOf(item) > -1;
              });
            });
          }
        });
      }
    },

    questionnaireSetLastSavedVersion(questionnaire) {
      this.lastSavedVersion = JSON.stringify(questionnaire);
    },

    initQuestionnaireList(list) {
      this.questionnaires = list;
    },

    initQuestionnaireChoices() {
      const signedBy = buildProfileChoices(true, i18n.global.t("Studea manager"));
      applyProfileChoicesChecked(signedBy, this.questionnaire.signedBy);
      this.questionnaire.signedBy = signedBy;

      if (this.questionnaire.marksVisibleBy) {
        const marksVisibleBy = buildProfileChoices();
        applyProfileChoicesChecked(marksVisibleBy, this.questionnaire.marksVisibleBy);
        this.questionnaire.marksVisibleBy = marksVisibleBy;
      }

      if (this.questionnaire.interviewDateVisibleBy) {
        const interviewDateVisibleBy = buildProfileChoices(true, i18n.global.t("Studea manager"));
        applyProfileChoicesChecked(interviewDateVisibleBy, this.questionnaire.interviewDateVisibleBy);
        this.questionnaire.interviewDateVisibleBy = interviewDateVisibleBy;
      }

      if (this.questionnaire.canBeCopiedBy) {
        const canBeCopiedBy = buildProfileChoices(true, usePlatformStore().studeaManagerProfileLabel.title);
        applyProfileChoicesChecked(canBeCopiedBy, this.questionnaire.canBeCopiedBy);
        this.questionnaire.canBeCopiedBy = canBeCopiedBy;
      }

      if (this.questionnaire.visibleBy) {
        const visibleBy = buildProfileChoices();
        applyProfileChoicesChecked(visibleBy, this.questionnaire.visibleBy);
        this.questionnaire.visibleBy = visibleBy;
      }

      if (this.questionnaire.versionItemMarksVisibleBy) {
        const versionItemMarksVisibleBy = buildProfileChoices();
        applyProfileChoicesChecked(versionItemMarksVisibleBy, this.questionnaire.versionItemMarksVisibleBy);
        this.questionnaire.versionItemMarksVisibleBy = versionItemMarksVisibleBy;
      }
    },

    fetchEditorQuestionnaire(payload) {
      return questionnaireManager.getAdminQuestionnaire(payload.id, payload.trainingCourseId).then((questionnaire) => {
        if (questionnaire) {
          this.questionnaire = questionnaire;
          this.questionnaireSetLastSavedVersion(questionnaire);
          this.initQuestionnaireChoices();
          this.questionnaireUpdate({
            questionnaire: this.questionnaire,
            save: false,
          });
        }
      });
    },

    fetchQuestionnaireList(trainingCourseId) {
      return questionnaireManager.getAdminQuestionnaireList(trainingCourseId).then((list) => {
        this.initQuestionnaireList(list);
      });
    },

    saveQuestionnaire(payload) {
      const questionnaire = questionnaireManager.processQuestionnaire(this.questionnaire);
      const summarySheetQuestionnaires = this.questionnaire.summarySheetQuestionnaires;

      if (summarySheetQuestionnaires && summarySheetQuestionnaires.length) {
        summarySheetQuestionnaires.forEach((summarySheet) => {
          if (!summarySheet.id) {
            delete summarySheet.id;
          }
        });
      } else {
        this.questionnaire.summarySheetQuestionnaires = [];
      }

      return new Promise((resolve, reject) => {
        questionnaireManager.postAdminQuestionnaire(questionnaire, payload.trainingCourseId, payload.id).then((res) => {
          if (!res.hasError) {
            resolve(res);
            notificationManager.showNotification("success", i18n.global.t("studea_manager.questionnaire.save_success"));
          } else {
            reject(res);
            notificationManager.showNotification("success", i18n.global.t("studea_manager.questionnaire.save_error"));
          }
        });
      });
    },
  },
});
